/**
 * Runs tab — a filterable table of per-run token/cost rollups over a date window
 * (from `useRuns`) with a click-through detail view rendering the run's labels,
 * its token/cost/log totals, and ONE of three views of the run itself.
 *
 * READ-ONLY: the console never mutates. All state is local (`useState`); there is
 * no router. The window/producer/label filters are applied on change and survive
 * a drill-in → Back round-trip (the detail view is a separate `openRun` state).
 *
 * ## The three views (issue #34, spec §5.5 of the 2026-10-27 design)
 *
 * A segmented control switches ONE region of the detail view between
 * <RunTimeline/> (the merged turns+logs timeline, the DEFAULT), the per-turn
 * table, and <LogsPanel/>. The old two surfaces are unchanged: each is the
 * right tool for a narrower question, and rendering the timeline *beside*
 * LogsPanel would put every log record on the screen twice.
 *
 * The choice is deliberately NOT persisted across drill-ins: `openRun` remounts
 * `RunDetail`, so every drill-in opens on the Timeline. Persisting it would be
 * the first piece of cross-drill-in state in a view whose filter state is
 * otherwise all in the tab above.
 *
 * <TracePanel/> is NOT one of the three. It stays mounted below the switch in
 * every view, collapsed, fetching only on first expand: the trace route derives
 * over the WHOLE session without pagination, and the switch must not make that
 * read eager (spec §5.6).
 *
 * ## The address is the producer
 *
 * Every drill-in is addressed by the run's `producer` (a Guardius identity_id).
 * `claimed_platforms` is what the run's records CLAIMED and is shown only as a
 * label. A run with no `producer` (a rollup written before the producer re-key)
 * has no drill-in route at all, so its row is not clickable.
 */

import { useState } from 'react';
import { useRuns, useTurns, type Run } from '../lib/queries';
import { serverNowMs } from '../lib/server-clock';
import LogsPanel from '../components/LogsPanel';
import RunTimeline from '../components/RunTimeline';
import TracePanel from '../components/TracePanel';
import {
  fmtCost,
  fmtTokens,
  TURN_COST_UNREPORTED,
  turnCostIsUnreported,
  UnpricedBadge,
} from '../lib/run-format';

// The row formatters live in `../lib/run-format` so `RunTimeline` can use them
// without an import cycle through this file. Re-exported here because UsageTab
// and RunsTab.test.tsx import them from this module.
export { fmtTokens, fmtCost, TURN_COST_UNREPORTED, turnCostIsUnreported, UnpricedBadge } from '../lib/run-format';

// ---------------------------------------------------------------------------
// Pure helpers (exported for unit tests — no DOM needed).
// ---------------------------------------------------------------------------

/** `YYYY-MM-DD` for `days` days before `end`, computed in UTC so it is stable
 *  regardless of the host timezone. `isoDaysAgo(0)` is today.
 *
 *  `end` defaults to GUARDIUS's now (`serverNowMs()`, #291), not the device's:
 *  the Runs and Usage tabs seed their default window from this, and a device
 *  whose date is wrong by a day used to open on the wrong window, silently
 *  excluding today. (The default alone cannot INVERT the window: both ends
 *  derive from one `end`. Inversion needs a user to type a correct `from`
 *  against the wrong default `to`, which #256 then answers as a confident
 *  empty result.) With no offset learned yet `serverNowMs()` IS the local clock, so
 *  this is the old behaviour. Only the DEFAULT moves: the inputs stay editable
 *  and an explicit `end` is used as given. Evaluated per call, so it reads the
 *  offset in force when the tab mounts — after auth has learned or restored it,
 *  since the tabs render only once a token is in state. */
export function isoDaysAgo(days: number, end: Date = new Date(serverNowMs())): string {
  const d = new Date(end.getTime() - days * 86_400_000);
  return d.toISOString().slice(0, 10);
}

/** Parse a space-separated `k=v` filter string into a label map. Tokens without
 *  exactly one non-empty key and value are ignored (malformed → dropped). */
export function parseLabelFilters(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const token of text.trim().split(/\s+/)) {
    if (!token) continue;
    const eq = token.indexOf('=');
    if (eq <= 0 || eq === token.length - 1) continue;
    const key = token.slice(0, eq);
    const value = token.slice(eq + 1);
    if (key && value) out[key] = value;
  }
  return out;
}

/** The run's claimed platforms as one label, or an em-dash when it claimed
 *  none. Display only: a claim is never an address. */
export function fmtClaimedPlatforms(run: Run): string {
  return run.claimed_platforms.length ? run.claimed_platforms.join(', ') : '—';
}

/** Newest-first: descending `run_date`, tie-broken by descending `last_seen`
 *  (missing `last_seen` sorts as ''). Returns a new array — never mutates. */
export function sortRuns(runs: Run[]): Run[] {
  return runs.slice().sort((a, b) => {
    if (a.run_date !== b.run_date) return a.run_date < b.run_date ? 1 : -1;
    const la = a.last_seen ?? '';
    const lb = b.last_seen ?? '';
    if (la === lb) return 0;
    return la < lb ? 1 : -1;
  });
}

// ---------------------------------------------------------------------------
// Run detail — meta, label pills, and one of three views of the run.
// ---------------------------------------------------------------------------

type DetailView = 'timeline' | 'turns' | 'logs';

/** The segmented control's buttons, in order. Buttons rather than `App`'s
 *  `<a href="#…">` tabs because this is local state, not a route — but the
 *  `.tab-bar`/`.tab` markup is the house idiom and is reused rather than
 *  re-invented. */
const DETAIL_VIEWS: { id: DetailView; label: string }[] = [
  { id: 'timeline', label: 'Timeline' },
  { id: 'turns', label: 'Turns' },
  { id: 'logs', label: 'Logs' },
];

/** Only a run that HAS an address reaches the detail view: `producer` is
 *  passed separately, already narrowed, so nothing below can fall back to
 *  something else to build a URL from. */
function RunDetail({ run, producer, onBack }: { run: Run; producer: string; onBack: () => void }) {
  const [view, setView] = useState<DetailView>('timeline');
  const { data, isLoading, isError, error, refetch } = useTurns(
    producer,
    run.session_id ?? '',
  );
  const turns = data?.events ?? [];
  const maxCost = turns.reduce((m, t) => Math.max(m, t.cost_usd), 0);
  const labels = Object.entries(run.labels ?? {});

  return (
    <section className="run-detail" aria-label={`Run ${run.session_id ?? producer}`}>
      <button type="button" className="btn" onClick={onBack}>
        ← Back to runs
      </button>

      <dl className="run-detail-meta">
        <div className="meta-row">
          <dt>producer</dt>
          <dd className="mono">{producer}</dd>
        </div>
        <div className="meta-row">
          <dt>claimed platforms</dt>
          <dd>{fmtClaimedPlatforms(run)}</dd>
        </div>
        <div className="meta-row">
          <dt>session</dt>
          <dd className="mono">{run.session_id ?? '—'}</dd>
        </div>
        <div className="meta-row">
          <dt>run date</dt>
          <dd>{run.run_date}</dd>
        </div>
        <div className="meta-row">
          <dt>models</dt>
          <dd>{run.models.length ? run.models.join(', ') : '—'}</dd>
        </div>
        <div className="meta-row">
          <dt>turns</dt>
          <dd>{run.turn_count}</dd>
        </div>
        {/* The token totals the header never carried (spec §2 goal 5). Every one
            goes through `fmtTokens`, so an ABSENT count is an em-dash rather
            than a `0` that would read as a measurement of zero. */}
        <div className="meta-row">
          <dt>input</dt>
          <dd className="mono">{fmtTokens(run.input_tokens)}</dd>
        </div>
        <div className="meta-row">
          <dt>output</dt>
          <dd className="mono">{fmtTokens(run.output_tokens)}</dd>
        </div>
        <div className="meta-row">
          <dt>cache read</dt>
          <dd className="mono">{fmtTokens(run.cache_read_tokens)}</dd>
        </div>
        <div className="meta-row">
          <dt>cache creation</dt>
          <dd className="mono">{fmtTokens(run.cache_creation_tokens)}</dd>
        </div>
        <div className="meta-row">
          <dt>cost</dt>
          <dd className="mono">
            {fmtCost(run.cost_usd)} <UnpricedBadge run={run} />
          </dd>
        </div>
        <div className="meta-row">
          <dt>logs</dt>
          <dd className="mono">{fmtTokens(run.log_count)}</dd>
        </div>
        {run.error_count !== undefined && run.error_count > 0 && (
          <div className="meta-row">
            <dt>errors</dt>
            <dd>
              <span className="pill pill-danger">{run.error_count} errors</span>
            </dd>
          </div>
        )}
      </dl>

      {labels.length > 0 && (
        <>
          <h3 className="section-title">Labels</h3>
          <div className="run-detail-labels">
            {labels.map(([k, v]) => (
              <span key={k} className="pill">
                {k}={v}
              </span>
            ))}
          </div>
        </>
      )}

      <nav className="tab-bar view-switch" aria-label="Run detail views">
        {DETAIL_VIEWS.map((v) => (
          <button
            key={v.id}
            type="button"
            className="tab"
            aria-current={v.id === view ? 'page' : undefined}
            onClick={() => setView(v.id)}
          >
            {v.label}
          </button>
        ))}
      </nav>

      {view === 'timeline' && (
        <RunTimeline
          producer={producer}
          sessionId={run.session_id ?? ''}
          /* REQUIRED and never defaulted — see RunTimeline's own prop doc. */
          runCostUsd={run.cost_usd}
        />
      )}

      {view === 'logs' && <LogsPanel producer={producer} sessionId={run.session_id ?? ''} />}

      {view === 'turns' && (
        <>
        <h3 className="section-title">Turns</h3>
        {isLoading ? (
          <p className="note">Loading turns…</p>
        ) : isError ? (
          <p className="error" role="alert">
            Failed to load turns: {error instanceof Error ? error.message : 'unknown error'}{' '}
            <button type="button" className="btn" onClick={() => refetch()}>
              Retry
            </button>
          </p>
        ) : turns.length === 0 ? (
          <p className="note">No turns recorded for this run.</p>
        ) : (
          <table className="detail-table">
            <thead>
              <tr>
                <th>ts</th>
                <th>model</th>
                <th className="detail-num">input</th>
                <th className="detail-num">output</th>
                <th className="detail-num">cache r/w</th>
                <th className="detail-num">cost</th>
                <th>cost bar</th>
              </tr>
            </thead>
            <tbody>
              {turns.map((t) => {
                // A missing per-turn cost renders as a placeholder, never as an
                // authoritative $0.0000 — and its bar is suppressed entirely, since
                // a flat zero-width bar reads as "this turn cost nothing".
                const unreported = turnCostIsUnreported(t.cost_usd, run.cost_usd, t.unpriced);
                return (
                  <tr key={t.sk}>
                    <td className="mono">{t.ts_iso}</td>
                    <td>{t.model}</td>
                    <td className="detail-num">{fmtTokens(t.input_tokens)}</td>
                    <td className="detail-num">{fmtTokens(t.output_tokens)}</td>
                    <td className="detail-num">
                      {fmtTokens(t.cache_read_tokens)} / {fmtTokens(t.cache_creation_tokens)}
                    </td>
                    <td className="detail-num mono">
                      {unreported
                        ? <span className="turn-cost-unreported" title={TURN_COST_UNREPORTED}>—</span>
                        : fmtCost(t.cost_usd)}
                    </td>
                    <td>
                      {!unreported && (
                        <span
                          className="turn-bar"
                          style={{ width: maxCost > 0 ? `${(t.cost_usd / maxCost) * 100}%` : '0%' }}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        </>
      )}

      {/* Collapsed by default and fetching only on first expand — the trace
          route derives over the WHOLE session and does not paginate, so an
          eager mount would make every drill-in pay a full-prefix S3 read
          (spec §6). See TracePanel's header. */}
      <TracePanel producer={producer} sessionId={run.session_id ?? ''} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// The tab.
// ---------------------------------------------------------------------------

export default function RunsTab() {
  // Default window from the SERVER clock via `isoDaysAgo`'s default `end`
  // (#291) — pass no `end` here, or the device clock comes back.
  const [from, setFrom] = useState(() => isoDaysAgo(7));
  const [to, setTo] = useState(() => isoDaysAgo(0));
  const [producer, setProducer] = useState('');
  const [labelText, setLabelText] = useState('');
  const [openRun, setOpenRun] = useState<Run | null>(null);

  const { data, isLoading, isError, error, refetch } = useRuns(
    from,
    to,
    producer || undefined,
    parseLabelFilters(labelText),
  );

  const controls = (
    <div className="card runs-controls">
      <label className="runs-control">
        <span className="runs-control-label">From</span>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
      </label>
      <label className="runs-control">
        <span className="runs-control-label">To</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
      </label>
      <label className="runs-control">
        <span className="runs-control-label">Producer</span>
        <input
          type="text"
          placeholder="any"
          value={producer}
          onChange={(e) => setProducer(e.target.value)}
        />
      </label>
      <label className="runs-control">
        <span className="runs-control-label">Labels</span>
        <input
          type="text"
          placeholder="team=alpha env=prod"
          value={labelText}
          onChange={(e) => setLabelText(e.target.value)}
        />
      </label>
    </div>
  );

  if (openRun?.producer) {
    return (
      <section className="runs-tab" aria-label="Runs">
        {controls}
        <RunDetail run={openRun} producer={openRun.producer} onBack={() => setOpenRun(null)} />
      </section>
    );
  }

  const runs = sortRuns(data?.runs ?? []);

  return (
    <section className="runs-tab" aria-label="Runs">
      {controls}

      {isLoading ? (
        <p className="note">Loading runs…</p>
      ) : isError ? (
        <p className="error" role="alert">
          Failed to load runs: {error instanceof Error ? error.message : 'unknown error'}{' '}
          <button type="button" className="btn" onClick={() => refetch()}>
            Retry
          </button>
        </p>
      ) : runs.length === 0 ? (
        <p className="note">No runs in this window.</p>
      ) : (
        <table className="detail-table runs-table">
          <thead>
            <tr>
              <th>producer</th>
              <th>claimed</th>
              <th>session</th>
              <th>date</th>
              <th>models</th>
              <th className="detail-num">turns</th>
              <th className="detail-num">input</th>
              <th className="detail-num">output</th>
              <th className="detail-num">cost</th>
              <th>flags</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => {
              // Both halves of the drill-in address, or no drill-in.
              const clickable = Boolean(run.session_id && run.producer);
              return (
                <tr
                  // The rollup's own identity, as its sort key builds it
                  // (`RUN#<date>#<producer>#<session>`): two producers may each
                  // own a session with the same id, and one session crossing
                  // midnight UTC is two rollups. `#` is a character no producer
                  // may carry, so the join is unambiguous.
                  key={`${run.run_date}#${run.producer ?? ''}#${run.session_id ?? ''}`}
                  data-session-id={run.session_id ?? ''}
                  className={clickable ? 'runs-row' : undefined}
                  style={clickable ? { cursor: 'pointer' } : undefined}
                  onClick={clickable ? () => setOpenRun(run) : undefined}
                >
                  <td className="mono">{run.producer ?? '—'}</td>
                  <td>{fmtClaimedPlatforms(run)}</td>
                  <td className="mono">{run.session_id ?? '—'}</td>
                  <td>{run.run_date}</td>
                  <td>{run.models.length ? run.models.join(', ') : '—'}</td>
                  <td className="detail-num">{run.turn_count}</td>
                  <td className="detail-num">{fmtTokens(run.input_tokens)}</td>
                  <td className="detail-num">{fmtTokens(run.output_tokens)}</td>
                  <td className="detail-num mono">{fmtCost(run.cost_usd)}</td>
                  <td>
                    {run.error_count !== undefined && run.error_count > 0 && (
                      <span className="pill pill-danger">{run.error_count} errors</span>
                    )}
                    <UnpricedBadge run={run} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
